"use client";

import Geocoding from "@mapbox/mapbox-sdk/services/geocoding";
import { createContext, useContext, useMemo, useRef } from "react";

import { DISABLE_GEOCODING } from "./env";

import type { GeocodeService } from "@mapbox/mapbox-sdk/services/geocoding";
import type { ReactNode } from "react";

export interface MapboxContext {
  accessToken: string;
  baseApiUrl: string;
  geocodeService: GeocodeService | null;
}

export const mapboxContext = createContext<MapboxContext | null>(null);

export const useMapboxContext = () => {
  const context = useContext(mapboxContext);

  if (context === null) {
    throw new Error("useMapboxContext must be used within a MapboxProvider");
  }
  return context;
};

export const useMapboxToken = () => {
  const context = useMapboxContext();
  return context.accessToken;
};

export const useMapboxHost = () => {
  const context = useMapboxContext();
  return context.baseApiUrl;
};

export const isMapboxAtlas = (mapboxHost: string | null | undefined): boolean => {
  return mapboxHost != null && !mapboxHost.includes("api.mapbox.com");
};

export const useMapboxAtlas = () => {
  const context = useMapboxContext();
  return isMapboxAtlas(context.baseApiUrl);
};

export const useGeocodeService = () => {
  const context = useMapboxContext();
  // React does not allow you to conditionally call hooks, so anything using this hook
  // with geocoding disabled should just get undefined instead.
  if (DISABLE_GEOCODING) return;

  return context.geocodeService;
};

export default function MapboxProvider({
  accessToken,
  host,
  children,
}: {
  accessToken: string;
  host: string;
  children: ReactNode;
}) {
  const geocodeServiceRef = useRef<GeocodeService | undefined>(undefined);

  if (geocodeServiceRef.current === undefined) {
    // Only init geocode service if mapbox token is non-empty
    geocodeServiceRef.current = accessToken ? Geocoding({ accessToken, origin: host }) : undefined;
  }

  const contextValue = useMemo(
    () => ({ accessToken, geocodeService: geocodeServiceRef.current ?? null, baseApiUrl: host }),
    [accessToken, host]
  );

  return <mapboxContext.Provider value={contextValue}>{children}</mapboxContext.Provider>;
}
